<template>
  <div>
    <div v-if="title || systemCount" class="flex items-center justify-between mb-1.5">
      <p v-if="title" class="text-xs font-medium text-fg-muted dark:text-fg-muted-dark uppercase tracking-wider">{{ title }}</p>
      <button
        v-if="systemCount"
        @click="showSystem = !showSystem"
        class="ml-auto text-xs text-fg-faint dark:text-fg-faint-dark hover:text-fg-secondary dark:hover:text-fg-secondary-dark transition-colors"
      >{{ showSystem ? 'Hide automated' : `Show automated (${systemCount})` }}</button>
    </div>

    <div v-if="loading" class="text-xs text-fg-faint dark:text-fg-faint-dark">Loading…</div>
    <div v-else-if="!events?.length" class="text-xs text-fg-faint dark:text-fg-faint-dark italic">{{ emptyText }}</div>
    <div v-else>
      <button
        v-if="hiddenCount && !newestFirst"
        @click="expandedAll = true"
        class="mb-1.5 text-xs text-blue-600 dark:text-blue-400 hover:underline"
      >Show {{ hiddenCount }} earlier</button>

      <template v-for="day in days" :key="day.key">
        <p class="text-[11px] font-medium text-fg-faint dark:text-fg-faint-dark mt-2 first:mt-0 mb-1">{{ day.label }}</p>
        <ul class="border-l border-stroke dark:border-stroke-dark ml-[9px] space-y-2">
          <li v-for="item in day.items" :key="item.key" class="relative pl-4 text-xs">
            <span
              :title="!item.run && isOpenedEmail(item.ev) ? 'Opened' : undefined"
              :class="['absolute -left-[9.5px] -top-px w-[18px] h-[18px] rounded-full flex items-center justify-center', badgeClass(item)]"
            >
              <component :is="iconFor(item)" v-if="iconFor(item)" class="w-3 h-3" />
              <span v-else class="w-1 h-1 rounded-full bg-current" />
            </span>

            <!-- Collapsed run of automated events -->
            <button
              v-if="item.run"
              @click="openRuns = new Set(openRuns).add(item.key)"
              class="text-left text-fg-faint dark:text-fg-faint-dark hover:text-fg-secondary dark:hover:text-fg-secondary-dark transition-colors"
            >▸ {{ item.run.length }} automated updates · {{ runSummary(item.run) }}</button>

            <!-- One line by default; click to reveal the detail (message, slots, …) -->
            <div
              v-else
              :class="meta(item.ev) && 'cursor-pointer'"
              @click="meta(item.ev) && toggleEvent(item.key)"
            >
              <p :class="labelClass(item.ev)">
                <span class="tabular-nums text-fg-faint dark:text-fg-faint-dark font-normal mr-1">{{ fmtTime(item.ev.at) }}</span>
                <span
                  v-if="item.ev.actor"
                  :title="item.ev.actor"
                  :style="{ background: avatarGradient(item.ev.actor) }"
                  class="ml-0.5 mr-1.5 inline-block px-1.5 rounded-full text-[10px] leading-4 font-medium text-white align-[1px]"
                >{{ firstName(item.ev.actor) }}</span>
                {{ item.ev.label }}
                <span v-if="item.ev.source" class="ml-1 px-1 rounded bg-surface-muted dark:bg-surface-muted-dark text-fg-muted dark:text-fg-muted-dark font-normal">{{ item.ev.source }}</span>
                <span v-if="meta(item.ev)" class="ml-0.5 text-fg-faint dark:text-fg-faint-dark font-normal">{{ openEvents.has(item.key) ? '▾' : '▸' }}</span>
              </p>
              <p v-if="meta(item.ev) && openEvents.has(item.key)" class="text-fg-faint dark:text-fg-faint-dark mt-0.5 break-words whitespace-pre-line">{{ meta(item.ev) }}</p>
            </div>
          </li>
        </ul>
      </template>

      <button
        v-if="hiddenCount && newestFirst"
        @click="expandedAll = true"
        class="mt-2 text-xs text-blue-600 dark:text-blue-400 hover:underline"
      >Show {{ hiddenCount }} older</button>
    </div>
  </div>
</template>

<script setup>
// Tiered activity timeline (signup card History + Directory sidebar Activity).
// Backend tags each event milestone/touch/system (utils/activity_timeline.py);
// here milestones are emphasized, touches shown plainly, and runs of 2+
// consecutive system events collapse into one summary row unless
// "Show automated" is on. The cap only ever hides the oldest items — never
// the middle — so recent notes can't get buried.
import { ref, computed } from 'vue'
import { avatarGradient } from '@/composables/useAvatarGradient.js'
import {
  AcademicCapIcon, ArrowPathIcon, ArrowUturnLeftIcon, BoltIcon, CalendarDaysIcon, ChatBubbleLeftIcon,
  ChatBubbleLeftRightIcon, CheckCircleIcon, CheckIcon, ClipboardDocumentListIcon, ClockIcon, EnvelopeIcon,
  EnvelopeOpenIcon, FlagIcon, InboxArrowDownIcon, PencilSquareIcon, PhoneArrowDownLeftIcon, PhoneIcon,
  QuestionMarkCircleIcon, QueueListIcon, TrashIcon, UserPlusIcon, XCircleIcon,
} from '@heroicons/vue/16/solid'

const props = defineProps({
  events:     { type: Array, default: () => [] },
  title:      { type: String, default: 'Activity' },
  loading:    { type: Boolean, default: false },
  emptyText:  { type: String, default: 'No activity on record' },
  newestFirst: { type: Boolean, default: true },
  limit:      { type: Number, default: 20 },
})

const showSystem  = ref(false)
const expandedAll = ref(false)
const openRuns    = ref(new Set())
const openEvents  = ref(new Set())

const toggleEvent = key => {
  const next = new Set(openEvents.value)
  next.has(key) ? next.delete(key) : next.add(key)
  openEvents.value = next
}

const GROUP_NAMES = {
  scheduling: ['scheduling', 'scheduling'],
  email:      ['email', 'emails'],
  teacher:    ['teacher notice', 'teacher notices'],
  reminder:   ['reminder', 'reminders'],
  text:       ['automated text', 'automated texts'],
  billing:    ['billing email', 'billing emails'],
  attendance: ['attendance', 'attendance'],
}

const toDate = iso => new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`)
const dayKey = iso => toDate(iso).toLocaleDateString('en-CA', { timeZone: 'America/New_York' })
const fmtTime = iso => toDate(iso).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' })

const sorted = computed(() => {
  const evs = [...(props.events || [])].sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0))
  return props.newestFirst ? evs.reverse() : evs
})

const systemCount = computed(() => sorted.value.filter(e => e.tier === 'system').length)

// Rows after collapsing: a run of 2+ consecutive system events becomes one row, filed
// under its first event's day (runs can span days — e.g. daily invite reminders).
const rows = computed(() => {
  // Keys come from the event itself (time + label), never its position, so a new
  // event arriving on a poll slots in without re-keying (and re-mounting) the rest.
  const seen = new Map()
  const evKey = ev => {
    const base = `${ev.at}:${ev.label}`
    const n = seen.get(base) || 0
    seen.set(base, n + 1)
    return n ? `${base}:${n}` : base
  }
  const out = []
  let run = []
  const flush = () => {
    if (!run.length) return
    // Anchor a run on its oldest event — new ones join at the recent end.
    const oldest = run.reduce((a, b) => (toDate(b.at) < toDate(a.at) ? b : a))
    const key = `run:${oldest.at}:${oldest.label}`
    if (run.length > 1 && !showSystem.value && !openRuns.value.has(key)) out.push({ key, run, at: run[0].at })
    else run.forEach(ev => out.push({ key: `ev:${evKey(ev)}`, ev, at: ev.at }))
    run = []
  }
  sorted.value.forEach(ev => {
    if (ev.tier === 'system') {
      run.push(ev)
    } else {
      flush()
      out.push({ key: `ev:${evKey(ev)}`, ev, at: ev.at })
    }
  })
  flush()
  return out
})

const hiddenCount = computed(() => (expandedAll.value ? 0 : Math.max(0, rows.value.length - props.limit)))

const visibleRows = computed(() => {
  if (!hiddenCount.value) return rows.value
  return props.newestFirst ? rows.value.slice(0, props.limit) : rows.value.slice(-props.limit)
})

const days = computed(() => {
  const today = dayKey(new Date().toISOString())
  const yesterday = dayKey(new Date(Date.now() - 864e5).toISOString())
  const thisYear = today.slice(0, 4)
  const out = []
  for (const row of visibleRows.value) {
    const key = dayKey(row.at)
    let day = out[out.length - 1]
    if (!day || day.key !== key) {
      const d = toDate(row.at)
      const label = key === today ? 'Today' : key === yesterday ? 'Yesterday'
        : d.toLocaleDateString('en-US', {
          timeZone: 'America/New_York', weekday: 'short', month: 'short', day: 'numeric',
          ...(key.slice(0, 4) !== thisYear && { year: 'numeric' }),
        })
      day = { key, label, items: [] }
      out.push(day)
    }
    day.items.push(row)
  }
  return out
})

const runSummary = run => {
  const counts = {}
  run.forEach(ev => { counts[ev.group] = (counts[ev.group] || 0) + 1 })
  const opened = run.filter(ev => ev.opened).length
  const parts = Object.entries(counts).map(([g, n]) => {
    const [one, many] = GROUP_NAMES[g] || ['update', 'updates']
    return `${n} ${n === 1 ? one : many}`
  })
  if (opened) parts.push(`${opened} opened`)
  const ends = [run[0].at, run[run.length - 1].at].map(dayKey)
  if (ends[0] !== ends[1]) {
    const [a, b] = [run[0].at, run[run.length - 1].at].sort().map(iso =>
      toDate(iso).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' }))
    parts.push(`${a}–${b}`)
  }
  return parts.join(', ')
}

// An opened email shows as an open envelope instead of the "opened" text.
const isOpenedEmail = ev => ev.kind === 'email' && ev.opened
const meta = ev => [ev.detail, ev.opened && !isOpenedEmail(ev) ? 'opened' : null].filter(Boolean).join(' · ')

// Actor pill shows first name only; an unresolved email falls back to its local part.
const firstName = actor => actor.split('@')[0].split(/\s+/)[0]

// Icon = what kind of event (backend `kind`, utils/activity_timeline.py); badge color =
// how much it matters (tier). Only milestone outcomes get green/red.
const KIND_ICONS = {
  sms: ChatBubbleLeftIcon, sms_thread: ChatBubbleLeftRightIcon,
  call: PhoneIcon, call_in: PhoneArrowDownLeftIcon,
  email: EnvelopeIcon, email_in: InboxArrowDownIcon, note: PencilSquareIcon, form: ClipboardDocumentListIcon,
  signup: UserPlusIcon, registered: CheckCircleIcon, lost: XCircleIcon, declined: XCircleIcon,
  waitlist: QueueListIcon, follow_up: FlagIcon, flag: FlagIcon, help: QuestionMarkCircleIcon,
  checkin: CheckIcon, attendance: CheckIcon, revert: ArrowUturnLeftIcon, teacher: AcademicCapIcon,
  slot: CalendarDaysIcon, slot_moved: ArrowPathIcon, slot_removed: TrashIcon, expired: ClockIcon,
}

const iconFor = item => {
  if (item.run) return BoltIcon
  return isOpenedEmail(item.ev) ? EnvelopeOpenIcon : KIND_ICONS[item.ev.kind]
}

const badgeClass = item => {
  const tier = item.run ? 'system' : item.ev.tier
  if (tier === 'milestone') {
    return {
      registered: 'bg-emerald-600 dark:bg-emerald-500 text-white',
      lost:       'bg-red-600 dark:bg-red-500 text-white',
    }[item.ev.kind] || 'bg-blue-600 dark:bg-blue-500 text-white'
  }
  return {
    touch:  'bg-surface-muted dark:bg-surface-muted-dark text-fg-secondary dark:text-fg-secondary-dark',
    system: 'bg-surface dark:bg-surface-dark border border-stroke dark:border-stroke-dark text-fg-faint dark:text-fg-faint-dark',
  }[tier]
}

const labelClass = ev => ({
  milestone: 'font-medium text-fg-base dark:text-fg-base-dark',
  touch:     'text-fg-secondary dark:text-fg-secondary-dark',
  system:    'text-fg-faint dark:text-fg-faint-dark',
}[ev.tier])
</script>
